/**
 * Skills API Service
 * Client for skills management endpoints
 */

import api from '../../../services/api';
import type {
  Skill,
  SkillFile,
  ChatbotSkill,
  CreateSkillRequest,
  UpdateSkillRequest,
  AssignSkillRequest,
  SkillCategory,
  SkillAnalytics,
} from '../types';

const API_BASE = `/skills`;

export class SkillsApi {
  /**
   * List all skills for current tenant
   */
  static async listSkills(params?: {
    category?: string;
    is_active?: boolean;
    search?: string;
  }): Promise<{ skills: Skill[]; total: number }> {
    const response = await api.get(API_BASE, { params });
    return response.data;
  }

  /**
   * List all skills available to current user: own tenant + shared
   */
  static async listAvailableSkills(params?: {
    category?: string;
    is_active?: boolean;
    search?: string;
  }): Promise<{ skills: Skill[]; total: number }> {
    const [ownedRes, sharedRes] = await Promise.allSettled([
      api.get(API_BASE, { params }),
      api.get(`${API_BASE}/shared`),
    ]);
    const owned: Skill[] = ownedRes.status === 'fulfilled' ? (ownedRes.value.data.skills || []) : [];
    const shared: Skill[] = sharedRes.status === 'fulfilled' ? (sharedRes.value.data.skills || []) : [];
    const seen = new Set(owned.map(s => s.skill_id));
    const taggedShared = shared.filter(s => !seen.has(s.skill_id)).map(s => ({ ...s, is_shared: true }));
    const merged = [...owned, ...taggedShared];
    return { skills: merged, total: merged.length };
  }

  /**
   * Get skill by ID
   */
  static async getSkill(skillId: string): Promise<Skill> {
    const response = await api.get(`${API_BASE}/${skillId}`);
    return response.data.skill;
  }

  /**
   * Create new skill
   */
  static async createSkill(data: CreateSkillRequest): Promise<Skill> {
    const response = await api.post(API_BASE, data);
    return response.data.skill;
  }

  /**
   * Update existing skill
   */
  static async updateSkill(skillId: string, data: UpdateSkillRequest): Promise<Skill> {
    const response = await api.put(`${API_BASE}/${skillId}`, data);
    return response.data.skill;
  }

  /**
   * Delete skill
   */
  static async deleteSkill(skillId: string): Promise<void> {
    await api.delete(`${API_BASE}/${skillId}`);
  }

  /**
   * Get skills assigned to a chatbot
   * @param chatbotId - The chatbot ID
   * @param forEditing - If true, returns ALL skills without tier filtering (for editing UI).
   *                     If false/undefined, filters by user tier (for conversation usage).
   */
  static async getChatbotSkills(chatbotId: string, forEditing?: boolean): Promise<ChatbotSkill[]> {
    const params = forEditing ? { for_editing: true } : undefined;
    const response = await api.get(`${API_BASE}/chatbot/${chatbotId}`, { params });
    return response.data.skills;
  }

  /**
   * Assign skill to chatbot
   */
  static async assignSkillToChatbot(
    chatbotId: string,
    data: AssignSkillRequest
  ): Promise<ChatbotSkill> {
    const response = await api.post(`${API_BASE}/chatbot/${chatbotId}/assign`, data);
    return response.data.chatbot_skill;
  }

  /**
   * Unassign skill from chatbot
   */
  static async unassignSkillFromChatbot(
    chatbotId: string,
    skillId: string
  ): Promise<void> {
    await api.delete(`${API_BASE}/chatbot/${chatbotId}/unassign/${skillId}`);
  }

  /**
   * Get skill analytics
   */
  static async getSkillAnalytics(params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<SkillAnalytics[]> {
    const response = await api.get(`${API_BASE}/analytics`, { params });
    return response.data.analytics;
  }

  /**
   * Get skill categories
   */
  static async getSkillCategories(): Promise<SkillCategory[]> {
    const response = await api.get(`${API_BASE}/categories`);
    return response.data.categories;
  }

  // =========================================================================
  // Skill File Attachments
  // =========================================================================

  /**
   * List files attached to a skill
   */
  static async getSkillFiles(skillId: string): Promise<SkillFile[]> {
    const response = await api.get(`${API_BASE}/${skillId}/files`);
    return response.data.files;
  }

  static async generateFilesContext(skillId: string): Promise<string | null> {
    const response = await api.post(`${API_BASE}/${skillId}/generate-files-context`);
    return response.data.files_context_prompt;
  }

  /**
   * Upload a file to a skill
   */
  static async uploadSkillFile(skillId: string, file: File): Promise<SkillFile> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`${API_BASE}/${skillId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.attachment;
  }

  /**
   * Delete a file from a skill
   */
  static async deleteSkillFile(skillId: string, attachmentId: string): Promise<void> {
    await api.delete(`${API_BASE}/${skillId}/files/${attachmentId}`);
  }

  /**
   * Toggle include_as_context for a skill file attachment
   */
  static async updateSkillFile(
    skillId: string,
    attachmentId: string,
    payload: { include_as_context: boolean }
  ): Promise<SkillFile> {
    const response = await api.patch(`${API_BASE}/${skillId}/files/${attachmentId}`, payload);
    return response.data.attachment;
  }

  /**
   * Toggle include_as_context for a skill's data bundle
   */
  static async updateSkillDataBundle(
    skillId: string,
    bundleId: string,
    payload: { include_as_context: boolean }
  ): Promise<{ success: boolean; include_as_context: boolean }> {
    const response = await api.patch(`${API_BASE}/${skillId}/data-bundles/${bundleId}`, payload);
    return response.data;
  }

  /**
   * Download a skill file
   */
  static async downloadSkillFile(skillId: string, attachmentId: string): Promise<Blob> {
    const response = await api.get(`${API_BASE}/${skillId}/files/${attachmentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }
}
